/**
 * Historique détaillé des positions d'un GRV, sur une période choisie — soit une plage de dates
 * précise (Du/Au), soit les X derniers jours — avec export Excel. Distinct de
 * `OngletMouvements.tsx` (transitions entre géozones, pas les positions brutes).
 *
 * `GET /v1/grv/{numero}/positions` accepte déjà `filter[debut]`/`filter[fin]` en simples chaînes
 * `YYYY-MM-DD` (même endpoint et même convention que `OngletCarteGrv.tsx`) : la comparaison
 * lexicographique des horodatages ISO côté serveur borne toute la journée sélectionnée, sans calcul
 * d'heure ici.
 */
import { useMemo, useState } from "react";

import { api, requete } from "../../shared/api/client";
import { useQuery } from "@tanstack/react-query";
import { Chargement, Erreur, Localisation, Vide } from "../../shared/composants/Etats";
import { EnTeteTriable, useTrie, useTriTableau } from "../../shared/composants/tableauTriable";
import { formaterDate } from "../../shared/composants/formatage";
import { BoutonExporterCsv } from "../../shared/composants/exportCsv";
import type { Position } from "../../shared/api/types";

// Bien au-delà de ce qu'un tracker peut produire sur une période raisonnable (un relevé toutes les
// quelques minutes sur plusieurs mois) : pas de pagination à l'écran, comme les autres exports
// (`docs/DECISIONS.md` D75), tant que cette échelle n'est pas dépassée.
const TAILLE_PAGE = 2000;

type ModePeriode = "derniers_jours" | "plage";
type Colonne = "timestamp" | "position_gps" | "zone_nom" | "statut_tracker" | "batterie_pct";

function ilYAJours(jours: number): string {
  const date = new Date();
  date.setDate(date.getDate() - jours);
  return date.toISOString().slice(0, 10);
}

// Même format que `TableauSejoursExport.tsx` (`positionGpsDe`, D91) : vide plutôt que "null, null"
// quand la position est absente ou agrégée (utilisateur Air Tahiti sur un site interne, D19/D46).
function positionGpsDe(position: Position): string {
  return position.latitude != null && position.longitude != null
    ? `${position.latitude}, ${position.longitude}`
    : "";
}

function valeurDe(position: Position, colonne: Colonne): string | number | null | undefined {
  if (colonne === "position_gps") return positionGpsDe(position);
  return position[colonne];
}

function localisationTexte(position: Position): string {
  if (!position.zone_nom) return "position inconnue";
  return position.localisation_agregee
    ? `${position.zone_nom} (localisation agrégée)`
    : position.zone_nom;
}

export function OngletMouvementsDetailles({ numero }: { numero: string }) {
  const [modePeriode, definirModePeriode] = useState<ModePeriode>("derniers_jours");
  const [derniersJours, definirDerniersJours] = useState("7");
  const [debut, definirDebut] = useState(() => ilYAJours(7));
  const [fin, definirFin] = useState(() => ilYAJours(0));
  const { tri, basculerTri } = useTriTableau<Colonne>({ colonne: "timestamp", descendant: true });

  const joursValides = Number(derniersJours);
  const periode =
    modePeriode === "derniers_jours"
      ? { debut: joursValides > 0 ? ilYAJours(joursValides) : null, fin: ilYAJours(0) }
      : { debut: debut || null, fin: fin || null };

  const requetePositions = useQuery({
    queryKey: ["positions", numero, "detaillees", periode.debut, periode.fin],
    queryFn: () =>
      api.lire<Position[]>(
        `/v1/grv/${encodeURIComponent(numero)}/positions` +
          requete({
            "filter[debut]": periode.debut,
            "filter[fin]": periode.fin,
            pageSize: TAILLE_PAGE,
          }),
      ),
    enabled: Boolean(periode.debut && periode.fin),
  });

  const positions = useMemo(() => requetePositions.data?.data ?? [], [requetePositions.data]);
  const triees = useTrie(positions, tri, valeurDe);

  return (
    <div>
      <div className="barre-de-filtres">
        <label htmlFor="mode-periode-mouvements">Période</label>
        <select
          id="mode-periode-mouvements"
          value={modePeriode}
          onChange={(e) => definirModePeriode(e.target.value as ModePeriode)}
        >
          <option value="derniers_jours">Derniers jours</option>
          <option value="plage">Du / au</option>
        </select>

        {modePeriode === "derniers_jours" ? (
          <>
            <label htmlFor="derniers-jours-mouvements">Nombre de jours</label>
            <input
              id="derniers-jours-mouvements"
              type="number"
              min="1"
              step="1"
              value={derniersJours}
              onChange={(e) => definirDerniersJours(e.target.value)}
            />
          </>
        ) : (
          <>
            <label htmlFor="mouvements-detailles-debut">Du</label>
            <input
              id="mouvements-detailles-debut"
              type="date"
              value={debut}
              max={fin}
              onChange={(e) => definirDebut(e.target.value)}
            />
            <label htmlFor="mouvements-detailles-fin">Au</label>
            <input
              id="mouvements-detailles-fin"
              type="date"
              value={fin}
              min={debut}
              max={ilYAJours(0)}
              onChange={(e) => definirFin(e.target.value)}
            />
          </>
        )}

        <BoutonExporterCsv
          nomFichier={`mouvements-detailles-${numero}`}
          colonnes={[
            { libelle: "Date/heure", valeur: (p: Position) => formaterDate(p.timestamp) },
            { libelle: "Géozone", valeur: localisationTexte },
            { libelle: "Statut tracker", valeur: (p: Position) => p.statut_tracker },
            { libelle: "Batterie (%)", valeur: (p: Position) => p.batterie_pct ?? null },
            { libelle: "Latitude", valeur: (p: Position) => p.latitude ?? null },
            { libelle: "Longitude", valeur: (p: Position) => p.longitude ?? null },
          ]}
          lignes={triees}
        />
      </div>

      {requetePositions.isPending && <Chargement libelle="Chargement des positions…" />}
      {requetePositions.isError && (
        <Erreur erreur={requetePositions.error} reessayer={() => void requetePositions.refetch()} />
      )}
      {requetePositions.data && triees.length === 0 && (
        <Vide libelle="Aucune position enregistrée sur cette période." />
      )}

      {triees.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              <EnTeteTriable colonne="timestamp" libelle="Date/heure" tri={tri} basculerTri={basculerTri} />
              <EnTeteTriable
                colonne="position_gps"
                libelle="Position GPS"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable colonne="zone_nom" libelle="Géozone" tri={tri} basculerTri={basculerTri} />
              <EnTeteTriable
                colonne="statut_tracker"
                libelle="Statut"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="batterie_pct"
                libelle="Batterie"
                tri={tri}
                basculerTri={basculerTri}
              />
            </tr>
          </thead>
          <tbody>
            {triees.map((position, index) => (
              <tr key={`${position.numero_grv}-${position.timestamp}-${index}`}>
                <td>{formaterDate(position.timestamp)}</td>
                <td>{positionGpsDe(position) || "—"}</td>
                <td>
                  <Localisation zone={position.zone_nom} agregee={position.localisation_agregee} />
                </td>
                <td>{position.statut_tracker}</td>
                <td>{position.batterie_pct ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
